import { matchPath } from 'react-router-dom';
import {
    canManageAbsenceTypes,
    canManageClients,
    canManageEmployees,
    canManageProjectTypes,
    canManageProjects,
    canManageRoles,
    canManageShiftTypes,
    canManageTagTypes,
    canManageIntegrations,
    canManageWorkCalendars,
    canImportTimeTracking,
    canSeeTimeBankReportPage,
    canSeeTimeReportPage,
    canSeeTimesheetPage,
    canSeeCostReportPage,
    canSeeVacationReportPage,
} from './entityPermissions';
import {
    canSeeClientsSection,
    canSeeEmployeesSection,
    canSeeProjectsSection,
    canSeeRolesSection,
    canSeeSettingsSection,
    canSeeTypesSection,
} from './navigationVisibility';
import { Profile } from './types';
import { getVisibleSettingsTabs } from '../../pages/Settings/tabs';
import { getVisibleTypeTabs } from '../../pages/Types/tabs';
import { NAVIGATION_ROUTES } from '../../routes/NavigationRoute';

interface RouteAccessRule {
    patterns: string[];
    canAccess: (profile: Profile | null | undefined) => boolean;
}

const ROUTE_ACCESS_RULES: RouteAccessRule[] = [
    {
        patterns: [NAVIGATION_ROUTES.SETTINGS],
        canAccess: canSeeSettingsSection,
    },
    {
        patterns: [NAVIGATION_ROUTES.SETTINGS_PREFERENCES],
        canAccess: canSeeSettingsPreferencesTab,
    },
    {
        patterns: [NAVIGATION_ROUTES.SETTINGS_INTEGRATIONS],
        canAccess: canSeeSettingsIntegrationsTab,
    },
    {
        patterns: [NAVIGATION_ROUTES.SETTINGS_EARLY_ACCESS],
        canAccess: canSeeSettingsEarlyAccessTab,
    },
    {
        patterns: [NAVIGATION_ROUTES.SETTINGS_CALENDAR],
        canAccess: canSeeSettingsCalendarTab,
    },
    {
        patterns: [NAVIGATION_ROUTES.SETTINGS_CALENDAR_CREATE, NAVIGATION_ROUTES.SETTINGS_CALENDAR_EDIT, NAVIGATION_ROUTES.SETTINGS_CALENDAR_DAY_EDIT],
        canAccess: canManageWorkCalendars,
    },
    {
        patterns: [NAVIGATION_ROUTES.SETTINGS_INTEGRATIONS_ZOHO_EDIT],
        canAccess: (profile) => false,//canSeeSettingsIntegrationsTab(profile) && canManageIntegrations(profile),
    },
    {
        patterns: [NAVIGATION_ROUTES.TYPES],
        canAccess: canSeeTypesSection,
    },
    {
        patterns: [NAVIGATION_ROUTES.PROJECT_TYPES],
        canAccess: canSeeProjectTypesSection,
    },
    {
        patterns: [NAVIGATION_ROUTES.CREATE_PROJECT_TYPE, NAVIGATION_ROUTES.EDIT_PROJECT_TYPE],
        canAccess: canManageProjectTypes,
    },
    {
        patterns: [NAVIGATION_ROUTES.SHIFT_TYPES],
        canAccess: canSeeShiftTypesSection,
    },
    {
        patterns: [NAVIGATION_ROUTES.CREATE_SHIFT_TYPE, NAVIGATION_ROUTES.EDIT_SHIFT_TYPE],
        canAccess: canManageShiftTypes,
    },
    {
        patterns: [NAVIGATION_ROUTES.ABSENCE_TYPES],
        canAccess: canSeeAbsenceTypesSection,
    },
    {
        patterns: [NAVIGATION_ROUTES.CREATE_ABSENCE_TYPE, NAVIGATION_ROUTES.EDIT_ABSENCE_TYPE],
        canAccess: canManageAbsenceTypes,
    },
    {
        patterns: [NAVIGATION_ROUTES.TAG_TYPES],
        canAccess: canSeeTagTypesSection,
    },
    {
        patterns: [NAVIGATION_ROUTES.CREATE_TAG_TYPE, NAVIGATION_ROUTES.EDIT_TAG_TYPE],
        canAccess: canManageTagTypes,
    },
    {
        patterns: [NAVIGATION_ROUTES.ROLES],
        canAccess: canSeeRolesSection,
    },
    {
        patterns: [NAVIGATION_ROUTES.CREATE_ROLE, NAVIGATION_ROUTES.EDIT_ROLE],
        canAccess: canManageRoles,
    },
    {
        patterns: [NAVIGATION_ROUTES.EMPLOYEES],
        canAccess: canSeeEmployeesSection,
    },
    {
        patterns: [NAVIGATION_ROUTES.CREATE_EMPLOYEE, NAVIGATION_ROUTES.EDIT_EMPLOYEE],
        canAccess: canManageEmployees,
    },
    {
        patterns: [NAVIGATION_ROUTES.CLIENTS],
        canAccess: canSeeClientsSection,
    },
    {
        patterns: [NAVIGATION_ROUTES.CREATE_CLIENT, NAVIGATION_ROUTES.EDIT_CLIENT],
        canAccess: canManageClients,
    },
    {
        patterns: [NAVIGATION_ROUTES.PROJECTS, NAVIGATION_ROUTES.PROJECTS_BY_TYPE],
        canAccess: canSeeProjectsSection,
    },
    {
        patterns: [NAVIGATION_ROUTES.CREATE_PROJECT, NAVIGATION_ROUTES.EDIT_PROJECT],
        canAccess: canManageProjects,
    },
    {
        patterns: [NAVIGATION_ROUTES.TIMESHEET],
        canAccess: canSeeTimesheetPage,
    },
    {
        patterns: [NAVIGATION_ROUTES.TIMESHEET_IMPORT],
        canAccess: canImportTimeTracking,
    },
    {
        patterns: [NAVIGATION_ROUTES.CREATE_SHIFT],
        canAccess: canSeeTimesheetPage,
    },
    {
        patterns: [NAVIGATION_ROUTES.EDIT_SHIFT, NAVIGATION_ROUTES.EDIT_ABSENCE],
        canAccess: canSeeTimesheetPage,
    },
    {
        patterns: [NAVIGATION_ROUTES.TIMEREPORT, NAVIGATION_ROUTES.TIMEREPORT],
        canAccess: canSeeTimeReportPage,
    },
    {
        patterns: [NAVIGATION_ROUTES.TIMEBANKREPORT, NAVIGATION_ROUTES.CREATE_TIME_BANK_TRANSACTION],
        canAccess: canSeeTimeBankReportPage,
    },
    {
        patterns: [NAVIGATION_ROUTES.VACATIONREPORT],
        canAccess: canSeeVacationReportPage,
    },
    {
        patterns: [NAVIGATION_ROUTES.CREATE_VACATION, NAVIGATION_ROUTES.EDIT_VACATION],
        canAccess: canSeeVacationReportPage,
    },
    {
        patterns: [NAVIGATION_ROUTES.COSTREPORT, NAVIGATION_ROUTES.CREATE_COST, NAVIGATION_ROUTES.EDIT_COST],
        canAccess: canSeeCostReportPage,
    },
];

export function isRouteAccessible(
    profile: Profile | null | undefined,
    pathname: string | null | undefined
): boolean {
    const normalizedPath = normalizePathname(pathname);
    if (!normalizedPath) {
        return true;
    }

    const routeRule = ROUTE_ACCESS_RULES.find((rule) =>
        rule.patterns.some((pattern) => matchPath({ path: pattern, end: true }, normalizedPath))
    );

    if (!routeRule) {
        return true;
    }

    return routeRule.canAccess(profile);
}

export function getForbiddenFallbackRoute(
    profile: Profile | null | undefined,
    pathname: string | null | undefined
): string {
    const normalizedPath = normalizePathname(pathname);

    if (normalizedPath.startsWith('/settings')) {
        return getVisibleSettingsTabs(profile)[0]?.route ?? NAVIGATION_ROUTES.PROFILE;
    }

    if (normalizedPath.startsWith('/types')) {
        return getVisibleTypeTabs(profile)[0]?.path ?? NAVIGATION_ROUTES.PROFILE;
    }

    return NAVIGATION_ROUTES.PROFILE;
}

function normalizePathname(pathname: string | null | undefined): string {
    if (!pathname) {
        return '';
    }

    return pathname.split('?')[0].split('#')[0];
}

function canSeeProjectTypesSection(profile: Profile | null | undefined): boolean {
    return getVisibleTypeTabs(profile).some((tab) => tab.path === NAVIGATION_ROUTES.PROJECT_TYPES);
}

function canSeeSettingsPreferencesTab(profile: Profile | null | undefined): boolean {
    return getVisibleSettingsTabs(profile).some((tab) => tab.route === NAVIGATION_ROUTES.SETTINGS_PREFERENCES);
}

function canSeeSettingsIntegrationsTab(profile: Profile | null | undefined): boolean {
    return getVisibleSettingsTabs(profile).some((tab) => tab.route === NAVIGATION_ROUTES.SETTINGS_INTEGRATIONS);
}

function canSeeSettingsEarlyAccessTab(profile: Profile | null | undefined): boolean {
    return getVisibleSettingsTabs(profile).some((tab) => tab.route === NAVIGATION_ROUTES.SETTINGS_EARLY_ACCESS);
}

function canSeeSettingsCalendarTab(profile: Profile | null | undefined): boolean {
    return getVisibleSettingsTabs(profile).some((tab) => tab.route === NAVIGATION_ROUTES.SETTINGS_CALENDAR);
}

function canSeeShiftTypesSection(profile: Profile | null | undefined): boolean {
    return getVisibleTypeTabs(profile).some((tab) => tab.path === NAVIGATION_ROUTES.SHIFT_TYPES);
}

function canSeeAbsenceTypesSection(profile: Profile | null | undefined): boolean {
    return getVisibleTypeTabs(profile).some((tab) => tab.path === NAVIGATION_ROUTES.ABSENCE_TYPES);
}

function canSeeTagTypesSection(profile: Profile | null | undefined): boolean {
    return getVisibleTypeTabs(profile).some((tab) => tab.path === NAVIGATION_ROUTES.TAG_TYPES);
}
